<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'

import { pivotEventsByDate } from '../service/pivotEventsByDate'

import {
  summaryPanelClass,
  summaryPanelTabsClass,
  summaryPanelTitleClass,
} from './summaryPanelClasses'
import SummaryPlatformEventsTable from './SummaryPlatformEventsTable.vue'

import {
  ReportSectionTitle,
  useReportData,
  useReportSchedules,
  type ReportSpec,
  type ScheduleTab,
} from '@/features/reports'
import { useI18n } from '@/shared/i18n'

const props = withDefaults(defineProps<{ defaultPostData?: string }>(), { defaultPostData: '' })
const activeTab = defineModel<string>('activeTab', { default: 'overall' })
const { t } = useI18n()
const schedules = useReportSchedules()
const channels = ref<string[]>(['overall'])

function scheduleTab(value: unknown): ScheduleTab | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as ScheduleTab)
    : null
}

const tab = computed(() => scheduleTab(schedules.checkScheduleTabs(50, 'events')))
const spec = computed<ReportSpec | null>(() => {
  const partitionName = tab.value?.partition_name
  if (!partitionName) return null
  return {
    report_id: 50,
    dimensions: ['marketing_channel', 'event_name'],
    filters:
      activeTab.value === 'overall'
        ? []
        : [{ field: 'marketing_channel', match_mode: 'equals', value: activeTab.value }],
    pagination_enabled: false,
    breakdown: 'date',
    partition_name: partitionName,
    ...(props.defaultPostData ? { defaultPostData: props.defaultPostData } : {}),
  }
})
const report = useReportData(spec, { immediate: false })
const pivot = computed(() => pivotEventsByDate(report.rows.value))

async function refreshChannels(): Promise<void> {
  const schedule = schedules.getScheduleByReportId(50)
  if (!schedule) return
  const cached = schedules.dropdownableDimensions(schedule.id, 'marketing_channel')?.values ?? []
  let values = cached
  try {
    if (!values.length)
      values = await schedules.loadDropdownValues(schedule.id, 'marketing_channel')
  } catch {
    values = cached
  }
  channels.value = ['overall', ...new Set(values.filter(Boolean))]
  if (!channels.value.includes(activeTab.value)) activeTab.value = 'overall'
}

onMounted(() => {
  void refreshChannels()
  void report.reload()
})
</script>

<template>
  <section :class="summaryPanelClass">
    <ReportSectionTitle
      :title="t('summary.platformEvents.title')"
      :tabs="['events']"
      :title-class="summaryPanelTitleClass"
      :tabs-class="summaryPanelTabsClass"
    />
    <SummaryPlatformEventsTable
      v-model:active-tab="activeTab"
      :pivot="pivot"
      :channels="channels"
      :state="report.state.value === 'idle' ? 'loading' : report.state.value"
      @retry="report.reload"
    />
  </section>
</template>
